/* eslint-disable no-console */

/**
 * @file The Navigator API enables your add-on to change the page displayed using JavaScript.
 * Old AMD key: `ac/navigator/module`
 */

import type { ConnectCallback } from '../../../types';
import { isInIframe } from '../spa-context';
import { getRoutes, hasRoutes } from './routes';
import type { NavigatorRouteFn, NavigatorContext, NavigatorTarget } from './types';
import { isApiEnabled, goToUrl, buildUrl, getTopmostSameOriginWindow } from './utils';

const THROTTLE_WAIT_LIMIT = 120000;
const REQUEST_LIMIT = 5;

/**
 * Navigates the user from the current page to the specified page. This call navigates the host product, not the iframe content.
 *
 * Requires a target location and the corresponding context. Navigating by passing a concrete url is currently unsupported.
 * @param target The type of page to navigate to.
 * @param context Specific information that identifies the page to navigate to.
 * @example
 *
 * // To navigate to the issue view page of an issue in Jira:
 * AP.navigator.go('issue', {
 *   issueKey: 'TEST-1'
 * });
 *
 * // To navigate to any page in the current site:
 * AP.navigator.go('site', {
 *   relativeUrl: '/browse/TEST-1'
 * });
 */
export const go = (
	target: NavigatorTarget,
	context: NavigatorContext,
	callback: ConnectCallback,
) => {
	const finalTarget = target.toLowerCase();
	const finalContext = context || {};

	if (!finalContext.addonKey) {
		finalContext.addonKey = callback._context.extension.addon_key;
	}

	if (!isApiEnabled()) {
		console.error('connect navigation api not yet implemented for this product');
		return;
	}

	const productRoutes = getRoutes();

	if (hasRoutes() && finalTarget in productRoutes) {
		const newRouteValue = productRoutes[finalTarget];

		if (typeof newRouteValue === 'function') {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(newRouteValue as NavigatorRouteFn)(finalContext, goToUrl);
		} else {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const url = buildUrl(newRouteValue as string, finalContext);
			goToUrl(url);
		}
	} else {
		console.error(
			`The URL target ${finalTarget} is not available. Valid targets are: ${Object.keys(
				productRoutes,
			).toString()}`,
		);
	}
};

/**
 * Returns boolean to prevent unlimited invocations of a method.
 *
 * Method uses local storage to temporarily persist data used to determine throttle
 * @private
 */
export const _shouldThrottle = (wait: number, reloadLimit: number): boolean => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	let lastReloadTimestamp = Number(window.localStorage.getItem('lastReloadTimestamp'));

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	let reloadAttempts = Number(window.localStorage.getItem('reloadAttempts'));
	const now = Date.now();

	// Reset reloadAttempts if last reload is out of range of throttle wait limit
	if (now - lastReloadTimestamp > wait) {
		reloadAttempts = 0;
	}

	// Prevent reload if limit has been reached
	if (reloadAttempts >= reloadLimit) {
		console.error(`Reloads must be ${wait}ms apart after ${reloadAttempts} reload attempts.`);
		return true;
	}

	lastReloadTimestamp = now;
	reloadAttempts += 1;

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	window.localStorage.setItem('lastReloadTimestamp', String(lastReloadTimestamp));

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	window.localStorage.setItem('reloadAttempts', String(reloadAttempts));

	return false;
};

/**
 * Triggers a reload of the parent page.
 * Throttle is applied to prevent infinite reloads.
 *
 * @example
 *
 * AP.navigator.reload();
 */
export const reload = () => {
	if (_shouldThrottle(THROTTLE_WAIT_LIMIT, REQUEST_LIMIT)) {
		return;
	}

	if (isInIframe()) {
		getTopmostSameOriginWindow(window).location.reload();
	} else {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.location.reload();
	}
};
